import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Col, Row } from 'react-bootstrap';
import { createTheme, ThemeProvider, Button } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { sub } from 'date-fns';
import { TextInput, SelectInput, StyledRadioInput } from '../../../Helpers/Inputs';
import { object, string, date } from '../../../Helpers/Validators/customValidators';
import States from '../../../Helpers/Lists/States';
import { getBasicInfo, setBasicInfo } from '../../../../sessionStorage';
import InputTheme from '../../Common/Helpers/InputTheme';
import { InputMaskDate } from '../../../Helpers/Formatters';
import ShieldCheckmarkIcon from '../../../../icons/shield-checkmark.svg?react';
import WorkerEmployeeIcon from '../../../../icons/workerEmployee.svg?react';
import WorkerBusinessOwnerIcon from '../../../../icons/workerBusinessOwner.svg?react';
import WorkerIndependentIcon from '../../../../icons/workerIndependent.svg?react';
import './AboutMe.scss';

export default function AboutMeForm(props) {

  const { onNext, ageSpecs } = props;
  const basicInfo = getBasicInfo();
  const [formState, setFormState] = useState(AboutMeForm.DefaultFormState(false, basicInfo));

  const formMethods = useForm({
    resolver: yupResolver(AboutMeForm.ValidationSchema(ageSpecs)),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: formState
  });

  const { handleSubmit, trigger } = formMethods;

  const onSubmit = (s) => {
    var basicInfoFromSessionStorage = getBasicInfo();
    basicInfoFromSessionStorage.gender = formState.gender;
    basicInfoFromSessionStorage.birthdate = formState.birthdate;
    basicInfoFromSessionStorage.state = formState.state;
    basicInfoFromSessionStorage.tobacco = formState.tobacco;
    basicInfoFromSessionStorage.employment = formState.employment;

    setBasicInfo(basicInfoFromSessionStorage);
    onNext();
  };

  const handleValueChange = (field, value) => {
    setFormState({ ...formState, [field]: value })
  };

  const handleGenderValueChange = (field, value) => {
    handleValueChange(field, value);
    trigger(field);
  };

  const handleEmploymentValueChange = (field, value) => {
    handleValueChange(field, value);
    trigger(field);
  };

  const getEmploymentOptions = [
    {
      label: "Employee",
      value: "Employee",
      secondaryText: "I work as a w-2 employee for a company.",
      icon: WorkerEmployeeIcon,
    },
    {
      label: "Business Owner",
      value: "Business",
      secondaryText: "I am an owner or partner in a business.",
      icon: WorkerBusinessOwnerIcon,
    },
    {
      label: "Independent Contractor",
      value: "Independent",
      secondaryText: "I work primarily as a 1099 independent contractor or am a freelancer.",
      icon: WorkerIndependentIcon,
    },
  ];

  const getTobaccoOptions = [
    {
      label: "Currently or previously used",
      value: "true",
    },
    {
      label: "Never used",
      value: "false",
    },
  ];

  return (
    <ThemeProvider theme={(theme) => createTheme({
      ...theme,
      ...InputTheme(),
    })}>
      <div className="basic-info-about-me income d-flex justify-content-center">
        <FormProvider {...formMethods}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row className="justify-content-center">
              <Col lg={12} className='w-100'>
                <StyledRadioInput
                  name="gender"
                  id="gender"
                  value={formState.gender}
                  options={[
                    { value: "F", label: "Female", icon: 'female' },
                    { value: "M", label: "Male", icon: 'male' }
                  ]}
                  onChange={e => handleGenderValueChange('gender', e)}
                  isBlankIcon={true}
                  isBlankCheckMark={true}
                />
                <div className='mb-3'>
                  <TextInput
                    name="birthdate"
                    id="birthdate"
                    label="Date of Birth"
                    value={formState.birthdate}
                    onChange={e => handleValueChange("birthdate", e.target.value)}
                    InputProps={{ inputComponent: InputMaskDate, }}
                  />
                </div>
                <div className='mb-3'>
                  <SelectInput
                    name="state"
                    id="state"
                    label="State"
                    value={formState.state}
                    onChange={e => handleValueChange("state", e.target.value)}
                    selectOptions={States()}
                  />
                </div>
                <div className='mb-3'>
                  <SelectInput
                    name="tobacco"
                    id="tobacco"
                    label="Tobacco/Nicotine Use"
                    value={formState.tobacco}
                    onChange={e => handleValueChange("tobacco", e.target.value)}
                    hideEmptyOption
                    selectOptions={getTobaccoOptions}
                  />
                </div>
                <div className='mb-3'>
                  <SelectInput
                    name="employment"
                    id="employment"
                    label="What type of worker are you?"
                    value={formState.employment}
                    onChange={e => handleEmploymentValueChange("employment", e.target.value)}
                    selectOptions={getEmploymentOptions}
                    hideEmptyOption
                    isImageOptions={true}
                    renderValue={value => value}
                    MenuProps={{ disableScrollLock: true }}
                  />
                </div>
              </Col>
              <Col className="d-flex m-0">
                <Button variant="contained" color="primary" type="submit" fullWidth className='submit-button'> <ShieldCheckmarkIcon className="button-icon" /> QUICK QUOTE</Button>
              </Col>
            </Row>
          </Form>
        </FormProvider>
      </div>
    </ThemeProvider>
  )
}

AboutMeForm.propTypes = {
  onNext: PropTypes.func.isRequired,
  ageSpecs: PropTypes.object.isRequired
};

AboutMeForm.ValidationSchema = (ageSpecs) => {
  return object().shape({
    gender: string().required("Gender is required."),
    birthdate: date()
      .typeError("Invalid date.")
      .max(sub(new Date(), { years: ageSpecs.min, days: ageSpecs.minAgeDays || 0 }, `Minimum age is ${ageSpecs.minAgeDays ? `${ageSpecs.minAgeDays} days` : ageSpecs.min}.`))
      .min(sub(new Date(), { years: ageSpecs.max + 1 }), `Maximum age is ${ageSpecs.max}.`)
      .required("Birtday is required"),
    state: string().required("State Code is required."),
    tobacco: string().required("This field is required."),
    employment: string().required("This field is required."),
  })
};

AboutMeForm.DefaultFormState = (prefill = false, basicInfo = null) => {
  return {
    gender: prefill ? 'M' : (basicInfo && basicInfo.gender) || '',
    birthdate: prefill ? '11/15/1980' : (basicInfo && basicInfo.birthdate) || '',
    tobacco: prefill ? 'false' : (basicInfo && basicInfo.tobacco) || 'false',
    state: prefill ? 'ID' : (basicInfo && basicInfo.state) || '',
    employment: prefill ? 'Employee' : (basicInfo && basicInfo.employment) || '',
  };
};
